import { gql } from '@apollo/client';

import {
  AlternativeWorkpointsBreakdown,
  ProgramSummaryParams,
} from 'spw-redux/modules/program-summary/types';
import { ProgramSummaryCardIcon } from 'spw-redux/modules/projects/types';
import { graphqlClient } from './utils';

export const getUsfTotal = (params: ProgramSummaryParams) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query usfTotal($buildingIds: [Int]!, $floorIds: [Int]!) {
        usfTotal(buildingIds: $buildingIds, floorIds: $floorIds) {
          circulation
          total
          subtotal
          circulationPercentage
        }
      }
    `,
    variables: params,
  });

export const getRsfTotal = (params: ProgramSummaryParams) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query rsfTotal($buildingIds: [Int]!, $floorIds: [Int]!) {
        rsfTotal(buildingIds: $buildingIds, floorIds: $floorIds) {
          totalRsf
          buildingRsfLoadFactor
        }
      }
    `,
    variables: params,
  });

export const getKeyData = (params: ProgramSummaryParams) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query keyData($buildingIds: [Int]!, $floorIds: [Int]!) {
        keyData(buildingIds: $buildingIds, floorIds: $floorIds) {
          meetingSeats
          workseatDensity
          circulationPercentage
          workseats
          total
          subtotal
        }
      }
    `,
    variables: { ...params },
  });

export const getSeatsOccupied = (
  params: ProgramSummaryParams,
  category: string
) =>
  graphqlClient.query({
    query: gql`
      query seatsOccupied(
        $buildingIds: [Int]!
        $floorIds: [Int]!
        $category: String!
      ) {
        seatsOccupied(
          buildingIds: $buildingIds
          floorIds: $floorIds
          category: $category
        ) {
          category
          daytime
          seatCount
          seatsInUse
          seatsOccupied
          spaceName
          spaceType
        }
      }
    `,
    variables: { ...params, category },
  });

export const getWorkplaceBehavior = (params: ProgramSummaryParams) =>
  graphqlClient.query({
    query: gql`
      query workplaceBehavior($buildingIds: [Int]!, $floorIds: [Int]!) {
        workplaceBehavior(buildingIds: $buildingIds, floorIds: $floorIds) {
          count
          daytime
          usage
        }
      }
    `,
    variables: params,
  });

export const getWorkspaceBreakdown = (params: ProgramSummaryParams) =>
  graphqlClient.query({
    query: gql`
      query workspaceBreakdown($buildingIds: [Int]!, $floorIds: [Int]!) {
        workspaceBreakdown(buildingIds: $buildingIds, floorIds: $floorIds) {
          privateOffices
          workstations
        }
      }
    `,
    variables: params,
  });

export const getSpacesBreakdown = (
  params: ProgramSummaryParams,
  category: string
) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query spacesBreakdown(
        $buildingIds: [Int]!
        $floorIds: [Int]!
        $category: String!
      ) {
        spacesBreakdown(
          buildingIds: $buildingIds
          floorIds: $floorIds
          category: $category
        ) {
          subcategory
          spaceType
          spaceTypeId
          quantity
          seats
          usf
          icon
          mixin
          hasRatio
          ratio
          usf_avg
          seats_capacity
          name
        }
      }
    `,
    variables: { ...params, category },
  });

export const getAltWorkpointsBreakdownApi = (params: ProgramSummaryParams) =>
  graphqlClient.query<{
    alternativeWorkpointsBreakdown: AlternativeWorkpointsBreakdown;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetAlternativeWorkpointsBreakdown(
        $buildingIds: [Int]!
        $floorIds: [Int]!
      ) {
        alternativeWorkpointsBreakdown: getAlternativeWorkpointsBreakdown(
          buildingIds: $buildingIds
          floorIds: $floorIds
        ) {
          individualWorkspaces {
            count
            seatsCount
          }
          meetingSpaces {
            count
            seatsCount
          }
          amenitySpaces {
            count
            seatsCount
          }
          supportSpaces {
            count
            seatsCount
          }
          all {
            count
            seatsCount
          }
        }
      }
    `,
    variables: params,
  });

export const updateSpacesByType = (payload: {
  spaceTypeId: number;
  usf?: number;
  seatCount?: number;
  floorIds: number[];
}) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation updateSpacesByType(
        $spaceTypeId: Int!
        $usf: Float
        $seatCount: Int
        $floorIds: [Int]!
      ) {
        updateSpacesByType(
          spaceTypeId: $spaceTypeId
          usf: $usf
          seatCount: $seatCount
          floorIds: $floorIds
        ) {
          id
        }
      }
    `,
    variables: payload,
  });

export const updateProgramSummaryCardIconApi = (
  projectId: number,
  cardIcon: ProgramSummaryCardIcon
) => {
  return graphqlClient.mutate<{ cardIcon: ProgramSummaryCardIcon }>({
    mutation: gql`
      mutation UpdateProgramSummaryCardIcon(
        $cardIcon: CardIconArgs!
        $projectId: ID!
      ) {
        cardIcon: updateProgramSummaryCardIcon(
          cardIcon: $cardIcon
          projectId: $projectId
        ) {
          projectId
          iconPath
          cardType
        }
      }
    `,
    variables: {
      projectId,
      cardIcon,
    },
  });
};
